<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'help-popup-overlay': true, 'is-visible': isVisible }"
    @click.stop.prevent="popupClicked">
    <div :class="{
      'help-popup': true,
      'is-visible': isVisible,
      'is-loading': isLoading,
    }">
      <a
        href="#"
        class="help-popup-close"
        @click.prevent="close()">
        &times;
      </a>

      <div
        v-if="isLoading"
        class="help-popup-empty-state">
        {{ $t('common.ui.loadingData') }}
      </div>

      <div
        class="help-popup-content"
        :data-toc-visible="(tocVisible).toString()">
        <div class="help-popup-content-sidebar">
          <h2 class="help-popup-content-sidebar-title">
            {{ $t('help.toc') }}

            <a
              href="#toggle-toc"
              @click.prevent="toggleToc">
              <template v-if="tocVisible">
                &times;
              </template>
              <template v-else>
                &equiv;
              </template>
            </a>
          </h2>
          <div
            ref="toc"
            class="help-popup-content-sidebar-wrapper">
            <ol>
              <li
                v-for="(item, index) of sidebar"
                :key="'help-sidebar-item-' + index"
                :data-id="item.id"
                :class="{
                  'is-expanded': expandedItem === item.id
                }">
                <a
                  :href="'#' + item.id"
                  @click.prevent="showHelpItem(item, false)">
                  {{ item.title }}
                </a>

                <ol v-if="item.subpages && item.subpages.length">
                  <li
                    v-for="(subitem, subindex) of item.subpages"
                    :key="'help-sidebar-item-' + index + '-' + subindex"
                    :data-id="subitem.id"
                    :class="{
                      'is-active': expandedSubItem === subitem.id
                    }">
                    <a
                      :href="'#' + item.id"
                      @click.prevent="showHelpItem(item, subitem)">
                      {{ subitem.title }}
                    </a>
                  </li>
                </ol>
              </li>
            </ol>
          </div>
        </div>
        <div
          ref="content"
          class="help-popup-content-html"
          v-pure-html="contentToDisplay"
          @click="handleInternalLinks">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import Vue from 'vue';

export default {
  name: 'help-popup',
  mixins: [
    PopupZindex
  ],
  computed: {
    isLoading () {
      return this.isLoadingSidebar || this.isLoadingContent;
    }
  },
  data () {
    return {
      content: null,
      idToOpenAfterLoad: false,
      isLoadingSidebar: true,
      isLoadingContent: true,
      isVisible: false,
      expandedItem: false,
      expandedSubItem: false,
      contentToDisplay: '<div class="select-topic-to-display">' + this.$t('help.selectTopicToDisplay') + '</div>',
      sidebar: null,
      tocVisible: false
    };
  },
  watch: {
    'isLoading': function (newValue) {
      if (!newValue && this.idToOpenAfterLoad) {
        Vue.nextTick(() => {
          this.openItemByID(this.idToOpenAfterLoad);
          this.idToOpenAfterLoad = false;
        });
      }
    }
  },
  mounted () {
    this.$bus.$on('show-help-popup', this.show);
    this.$bus.$on('hide-help-popup', this.close);
  },
  methods: {
    show (idToOpen) {
      this.isVisible = true;

      if (this.isLoading) {
        this.loadData();
      }

      this.increasePopupZindex();

      if (!idToOpen) {
        this.tocVisible = true;
        this.expandedItem = false;
        this.expandedSubItem = false;
        this.contentToDisplay = '<div class="select-topic-to-display">' + this.$t('help.selectTopicToDisplay') + '</div>';
        document.querySelector('.help-popup-content-sidebar-wrapper').scrollTop = 0;
      } else {
        this.tocVisible = false;

        if (this.isLoading) {
          this.idToOpenAfterLoad = idToOpen;
        } else {
          this.idToOpenAfterLoad = false;

          if (idToOpen) {
            this.openItemByID(idToOpen);
          }
        }
      }
    },
    loadData () {
      this.$http.get('/panel/help/sidebar').then(response => {
        this.isLoadingSidebar = false;
        this.sidebar = response.data;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/help/sidebar', this.$router, this.$bus, this.loadData.bind(this));
        this.isLoadingSidebar = false;
      });

      this.$http.get('/panel/help/content').then(response => {
        this.isLoadingContent = false;
        this.content = response.data;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/help/sidebar', this.$router, this.$bus, this.loadData.bind(this));
        this.isLoadingContent = false;
      });
    },
    close () {
      this.isVisible = false;
      this.removePopupZindex();
    },
    showHelpItem (item, subitem = false, scrollMenu = false) {
      this.expandedItem = item.id;
      let itemData;

      if (subitem) {
        itemData = this.content.filter(helpItem => helpItem.id === subitem.id);
        this.expandedSubItem = subitem.id;
      } else {
        itemData = this.content.filter(helpItem => helpItem.id === item.id);
        this.expandedSubItem = false;
      }

      if (itemData[0]) {
        this.contentToDisplay = '<h2>' + itemData[0].title + '</h2>' + itemData[0].content;
      }

      Vue.nextTick(() => {
        this.$refs['content'].scrollTop = 0;
        this.tocVisible = false;

        if (scrollMenu) {
          document.querySelector('.help-popup-content-sidebar-wrapper li[data-id="' + item.id + '"]').scrollIntoView();
        }
      });
    },
    handleInternalLinks (e) {
      let clickedLink = false;

      if (e.target && e.target.tagName === 'A') {
        clickedLink = e.target;
      } else if (e.target && e.target.parentNode && e.target.parentNode.tagName === 'A') {
        clickedLink = e.target.parentNode;
      }

      if (!clickedLink) {
        return;
      }

      if (clickedLink.getAttribute('href').indexOf('bookero-docs/?page_id=') > -1) {
        e.preventDefault();
        let idToOpen = parseInt(clickedLink.getAttribute('href').split('bookero-docs/?page_id=')[1], 10);
        this.openItemByID(idToOpen);
      } else if (clickedLink.getAttribute('href').indexOf('app.bookero.pl') > -1) {
        e.preventDefault();
        let pathToOpen = clickedLink.getAttribute('href');
        pathToOpen = pathToOpen.split('app.bookero.pl')[1];
        this.$router.push(pathToOpen);
        this.close();
      } else {
        window.open(clickedLink.getAttribute('href'), '_blank');
      }
    },
    openItemByID (idToOpen) {
      let itemToOpen = this.content.filter(helpItem => helpItem.id === idToOpen);
      let parentItem = this.sidebar.filter(sidebarItem => sidebarItem.id === idToOpen || sidebarItem.subpages.filter(subpageItem => subpageItem.id === idToOpen).length > 0);

      if (parentItem && parentItem[0] && parentItem[0].id === idToOpen) {
        this.showHelpItem(itemToOpen[0], false, true);
      } else if (itemToOpen && itemToOpen[0]) {
        this.showHelpItem(parentItem[0], itemToOpen[0], true);
      }
    },
    toggleToc () {
      this.tocVisible = !this.tocVisible;
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-help-popup', this.show);
    this.$bus.$off('hide-help-popup', this.close);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.help-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  height: 95vh;
  max-width: 1200px;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  scrollbar-width: 10px;
  scrollbar-color: rgba(38, 150, 233, .5) transparent;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 95vw;
  z-index: 10000;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(38, 150, 233, .5);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: $color-primary;
  }

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;
    z-index: 10000;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &.is-visible {
    display: block;
  }

  &.is-loading {
    h2,
    p,
    dl {
      opacity: 0;
    }
  }

  .help-popup-empty-state {
    align-items: center;
    background: #fff;
    color: $color-text-light;
    display: flex;
    font-size: 2.4rem;
    height: 100%;
    justify-content: center;
    left: 0;
    padding: 3.2rem 0;
    position: absolute;
    text-align: center;
    top: 0;
    width: 100%;
    z-index: 10;
  }

  &-content {
    display: flex;
    height: 100%;
    left: 0;
    overflow: auto;
    position: absolute;
    -webkit-overflow-scrolling: touch;
    top: 0;
    width: 100%;

    &-sidebar {
      background: $color-bg-light;
      border-radius: $border-radius-small 0 0 $border-radius-small;
      font-size: 1.4rem;
      font-weight: 600;
      height: 100%;
      width: 360px;

      &-title {
        align-items: center;
        box-shadow: 0 5px 5px rgba(0, 0, 0, .04);
        display: flex;
        font-weight: 400;
        height: 66px;
        margin: 0;
        padding: 1rem 1.25rem;
      }

      &-wrapper {
        height: calc(100% - 66px);
        overflow: auto;

        & > ol {
          list-style-type: none;
          margin: 0;
          padding: 0 0 2rem 0;

          & > li {
            display: block;
            font-weight: 400;
            position: relative;

            &:before {
              background: $color-border;
              content: "";
              left: 14px;
              height: 100%;
              position: absolute;
              top: 0;
              width: 2px;
            }

            &:after {
              background: $color-border;
              border-radius: 5px;
              content: "";
              height: 10px;
              left: 10px;
              position: absolute;
              top: 13px;
              width: 10px;
            }

            &:last-child {
              &:before {
                height: 13px;
                top: 0;
              }
            }

            &.is-expanded {
              & > a {
                font-weight: bold;
              }

              &:after {
                background: $color-notice;
              }
            }

            & > a {
              display: block;
              padding: 10px 20px 10px 30px;
            }

            & > ol {
              display: none;
              list-style-type: none;
              margin: 0;
              padding: 0;

              & > li {
                display: block;
                position: relative;

                &:before {
                  background: $color-border;
                  content: "";
                  left: 44px;
                  height: 100%;
                  position: absolute;
                  top: 0;
                  width: 2px;
                }

                &:after {
                  background: $color-border;
                  border-radius: 5px;
                  content: "";
                  height: 10px;
                  left: 40px;
                  position: absolute;
                  top: 13px;
                  width: 10px;
                }

                &:last-child {
                  &:before {
                    height: 13px;
                    top: 0;
                  }
                }

                & > a {
                  display: block;
                  padding: 10px 20px 10px 60px;
                }

                &.is-active {
                  & > a {
                    font-weight: bold;
                  }

                  &:after {
                    background: $color-notice;
                  }
                }
              }
            }

            &.is-expanded {
              & > ol {
                display: block;
              }
            }
          }
        }
      }
    }

    &-html {
      height: 100%;
      overflow: auto;
      padding: 2rem 3rem;
      position: relative;
      width: calc(100% - 360px);

      /deep/ h2:first-child {
        font-weight: 400;
        margin-top: 0;
      }

      /deep/ h1,
      /deep/ h2,
      /deep/ h3,
      /deep/ h4 {
        font-weight: 400;
      }

      /deep/ .select-topic-to-display {
        align-items: center;
        display: flex;
        font-size: 3.2rem;
        height: 100%;
        justify-content: center;
        left: 0;
        opacity: .5;
        position: absolute;
        top: 0;
        width: 100%;
      }

      /deep/ img {
        height: auto;
        max-width: 100%;
      }

      /deep/ ul {
        padding: .5rem 0 .5rem 2rem;

        li {
          padding: .5rem 0;
        }
      }

      /deep/ p {
        line-height: 1.4;
      }

      /deep/ figure {
        margin: 2rem auto;
      }

      /deep/ .is-style-tip,
      /deep/ .is-style-warning {
        background: $color-notice;
        border-radius: 10px;
        color: #fff;
        margin-top: 3rem;
        padding: 2rem 1rem 1.5rem 1.5rem;
        position: relative;

        & > strong:first-child {
          background: #fff;
          border: 2px solid $color-notice;
          border-radius: 10px;
          color: $color-notice;
          padding: 3px 10px;
          position: absolute;
          top: -13px;
        }

        a {
          color: #fff;
          text-decoration: underline;

          &:active,
          &:focus,
          &:hover {
            text-decoration: none;
          }
        }
      }

      /deep/ .is-style-warning {
        background: $color-warning;

        & > strong:first-child {
          background: #fff;
          border: 2px solid $color-warning;
          color: $color-warning;
        }
      }
    }
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
    z-index: 1;
  }

  a[href="#toggle-toc"] {
    display: none;
  }
}

@media (max-width: 1024px) {
  .help-pupup-content-sidebar {
    width: 240px;
  }

  .help-popup-content-html {
    width: calc(100% - 240px);
  }
}

@media (max-width: 840px) {
  .help-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    max-height: 100%;
    padding: 0!important;
    top: 0;
    transform: none;
    width: 100vw;

    a[href="#toggle-toc"] {
      border: 2px solid $color-notice;
      display: block;
      font-size: 3.2rem;
      height: 24px;
      line-height: .45;
      position: absolute;
      right: -5.3rem;
      text-align: center;
      top: 2.2rem;
      transition: right .2s ease-out;
      width: 24px;
      z-index: 1;
      border-radius: 5px;
    }

    .help-popup-content {
      height: 100%;
      max-height: 100%;
      margin-top: 0;

      &[data-toc-visible="false"] {
        .help-popup-content-sidebar {
          left: -300px;
        }
      }

      &[data-toc-visible="true"] {
        left: 0;

        a[href="#toggle-toc"] {
          font-size: 2.4rem;
          font-weight: bold;
          line-height: .825;
          right: 1.5rem;
        }
      }
    }

    .help-popup-content-sidebar {
      left: 0;
      position: absolute;
      top: 0;
      transition: left .2s ease-out;
      width: 300px;
      z-index: 1;

      &-title {
        position: relative;
      }
    }

    .help-popup-content-html {
      width: 100%;

      /deep/ .select-topic-to-display {
        left: 300px;
        padding: 20px;
        text-align: center;
        width: calc(100% - 300px);
      }

      /deep/ h2:first-child {
        padding-left: 32px;
      }
    }

    .help-popup-close {
      right: 1rem;
      top: 1rem;
    }
  }
}

@media (max-width: 640px) {
  .help-popup {

    .help-popup-content {
      height: 100%;
      margin-top: 0rem;
    }

    .help-popup-content[data-toc-visible="true"] {
      .help-popup-content-html {
        width: 100%;

        /deep/ .select-topic-to-display {
          display: none;
        }
      }
    }

    .help-popup-content[data-toc-visible="false"] {
      .help-popup-content-html {
        /deep/ .select-topic-to-display {
          left: 0;
          width: 100%;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Help.vue